import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import Paper from '@mui/material/Paper';
import { Fragment } from 'react';
import { GameGuideHelpButton } from '../../../components/gameGuideHelpButton';
import { TwemojiWrapper } from '../../../components/twemojiWrapper';
import { BOLD_FONT_FAMILY } from '../../../constants/constants';
import { PanelContentContainer } from '../../../panels/panelContentContainer';
import { PanelHeader } from '../../../panels/panelHeader';
import { PanelItemDivider } from '../../../panels/panelItemDivider';
import { useAppSelector } from '../../../redux/store';
import { PLAYER_INFO_STAT_VALUE_COLOR, ROOM_INFO_PANEL_LIST_BACKGROUND_COLOR, ROOM_INFO_PANEL_LIST_ITEM_DIVIDER_COLOR, ROOM_INFO_PANEL_SECTION_BACKGROUND_COLOR, ROOM_INFO_PANEL_SECTION_SUBTLE_BACKGROUND_COLOR } from '../../../styles/colors';
import { ALL_POKER_VARIANT_PROPERTIES } from '../../../types/pokerVariantTypes';
import { TOURNEY_CHIP_CHAR } from '../../../utils/currencyUtils';
import { isLocalhostEnvironment, isTestEnvironment } from '../../../utils/utils';
import { PokerRoomInfoPayoutsSubPanel } from './pokerRoomInfoPayoutsSubPanel';
const PAPER_CSS = css `
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: ${ROOM_INFO_PANEL_LIST_BACKGROUND_COLOR} !important;
`;
const ROOM_NAME_CSS = css `
  font-family: ${BOLD_FONT_FAMILY};
  font-size: 125%;
`;
const VARIANT_ROW_CSS = css `
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;
const FOOTER_CSS = css `
  font-size: 75%;
  color: #555;
`;
export const POKER_ROOM_INFO_ITEM_CONTAINER_CSS = css `
  font-size: 85%;
  padding: 3px;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  gap: 5px;
`;
export const POKER_ROOM_INFO_ITEM_VALUE_CSS = css `
  color: ${PLAYER_INFO_STAT_VALUE_COLOR};
  flex-grow: 1;
  text-align: right;
`;
const SECTION_CONTAINER_CSS = css `
  background-color: ${ROOM_INFO_PANEL_SECTION_BACKGROUND_COLOR};
  border-radius: 10px;
  padding: 5px;
  font-size: 85%;
`;
export const POKER_ROOM_INFO_SUBTLE_SECTION_CONTAINER_CSS = css(SECTION_CONTAINER_CSS, css `
    background-color: ${ROOM_INFO_PANEL_SECTION_SUBTLE_BACKGROUND_COLOR};
  `);
const WELCOME_MESSAGE_CSS = css(SECTION_CONTAINER_CSS, css `
    white-space: pre-wrap;
  `);
export const POKER_ROOM_INFO_SUB_SECTION_HEADER_CSS = css `
  font-family: ${BOLD_FONT_FAMILY};
`;
function getVariantGameGuidePage(isAllInGame, isMixedGame, variantGameGuidePageType) {
    if (isAllInGame) {
        return 'all-insanity';
    }
    if (isMixedGame) {
        return 'mixed-games';
    }
    return variantGameGuidePageType;
}
export const PokerRoomInfoSubPanel = () => {
    // TODO: Improve this inefficient selector:
    const roomInfo = useAppSelector((state) => state.poker.roomInfo);
    const isAllInGame = useAppSelector((state) => !!state.poker.isAllInGame);
    const isMixedGame = useAppSelector((state) => !!state.poker.isMixedGame);
    const variantGameGuidePageType = useAppSelector((state) => {
        if (state.poker.pokerVariant) {
            return ALL_POKER_VARIANT_PROPERTIES[state.poker.pokerVariant].gameGuidePageType;
        }
    });
    if (!roomInfo) {
        return null;
    }
    const gameGuidePage = getVariantGameGuidePage(isAllInGame, isMixedGame, variantGameGuidePageType);
    const { roomName, variantName, infoFields, payoutsInfo, mttTables, roomOpenSince, roomCreatorIdName, roomCode } = roomInfo;
    let { welcomeMessage } = roomInfo;
    if (isLocalhostEnvironment() && !isTestEnvironment() && !welcomeMessage) {
        welcomeMessage =
            'This...\nis a mock welcome message for this room that is spread out onto multiple explicit lines.\n\nWelcome message ends.';
    }
    return (_jsxs(_Fragment, { children: [_jsx(PanelHeader, { title: 'Room Info' }), _jsxs(Paper, { "aria-label": 'room info', component: 'section', css: PAPER_CSS, children: [_jsxs(PanelContentContainer, { children: [_jsx(TwemojiWrapper, { children: _jsx("span", { css: ROOM_NAME_CSS, children: roomName }) }), _jsxs("div", { css: VARIANT_ROW_CSS, children: [variantName, gameGuidePage ? _jsx(GameGuideHelpButton, { pageType: gameGuidePage, title: 'More Info' }) : null] })] }), welcomeMessage && (_jsx(PanelContentContainer, { children: _jsx("div", { css: WELCOME_MESSAGE_CSS, children: welcomeMessage }) })), _jsx(PanelContentContainer, { children: infoFields.map(([key, value], i) => {
                            return (_jsxs(Fragment, { children: [i > 0 ? (_jsx(PanelItemDivider, { color: ROOM_INFO_PANEL_LIST_ITEM_DIVIDER_COLOR, thicknessPx: 1, marginPx: 0 })) : null, _jsxs("div", { css: POKER_ROOM_INFO_ITEM_CONTAINER_CSS, "aria-hidden": true, "aria-label": `${key}: ${value}`, children: [_jsx("span", { children: key }), _jsx("span", { css: POKER_ROOM_INFO_ITEM_VALUE_CSS, children: value })] })] }, i));
                        }) }), mttTables && mttTables.length > 0 && (_jsx(PanelContentContainer, { children: _jsxs("div", { css: POKER_ROOM_INFO_SUBTLE_SECTION_CONTAINER_CSS, children: [_jsx("div", { css: POKER_ROOM_INFO_SUB_SECTION_HEADER_CSS, children: "Tables" }), _jsx(PanelItemDivider, { color: ROOM_INFO_PANEL_LIST_ITEM_DIVIDER_COLOR, thicknessPx: 3, marginPx: 0 }), mttTables.map(({ tableName, numPlayers, avgStackSize }, i) => (_jsxs(Fragment, { children: [i > 0 ? (_jsx(PanelItemDivider, { color: ROOM_INFO_PANEL_LIST_ITEM_DIVIDER_COLOR, thicknessPx: 3, marginPx: 0 })) : null, _jsx("div", { children: tableName }), _jsxs("div", { children: [numPlayers, " Player", numPlayers != 1 ? 's' : ''] }), _jsxs("div", { children: ["Avg. Stack: ", TOURNEY_CHIP_CHAR, avgStackSize.toLocaleString()] })] }, i)))] }) })), _jsx(PokerRoomInfoPayoutsSubPanel, { payoutsInfo: payoutsInfo }), _jsx(PanelContentContainer, { children: _jsxs("div", { css: FOOTER_CSS, children: [roomCreatorIdName && _jsxs("div", { children: ["Room Created By: ", roomCreatorIdName.name] }), _jsxs("div", { children: ["Room Opened: ", roomOpenSince] }), _jsxs("div", { children: ["Room Code: ", roomCode] })] }) })] })] }));
};
